/**
 * Rule-context allow-lists of the Contrats pages.
 *
 * - « Mes contrats » exposes the extranet base context only.
 * - The contract detail adds the contract's primitives, so a rule can e.g.
 *   hide the premiums of a terminated contract or rename a field for one
 *   insurer.
 *
 * Registrations pass these as `contextVariables`; the pages build the bag
 * with {@link buildContractDetailRuleContext} (memoised on primitives).
 */

import type { ContextVariableDescriptor } from '@components/shared/configurable';

import type { ContractDetail } from '../../api/contractsService';
import { extranetBaseContextSchema } from '../../contextVariables';

/** Context of « Mes contrats ». */
export const contractsListContextSchema = {
  ...extranetBaseContextSchema,
} as const satisfies Record<string, ContextVariableDescriptor>;

/** Context of the contract detail page. */
export const contractDetailContextSchema = {
  ...extranetBaseContextSchema,
  contract_id: {
    type: 'string',
    description: 'Identifiant VEOS du contrat affiché (ID_NPOL).',
    sourceField: 'ID_NPOL',
  },
  contract_status: {
    type: 'string',
    description: 'Position du contrat (CD_POS de WS2_EA_FIC_POL), p. ex. « EN COURS ».',
    sourceField: 'CD_POS',
  },
  contract_product: {
    type: 'string',
    description: 'Code produit du contrat (CD_PROD), p. ex. « CMI ».',
    sourceField: 'CD_PROD',
  },
  contract_product_label: {
    type: 'string',
    description: 'Libellé du produit (LIB_PROD).',
    sourceField: 'LIB_PROD',
  },
  insurer: {
    type: 'string',
    description: "Nom de l'assureur du contrat (NOM_CIE).",
    sourceField: 'NOM_CIE',
  },
} as const satisfies Record<string, ContextVariableDescriptor>;

/** Keys of {@link contractDetailContextSchema} beyond the base ones. */
export type ContractDetailContextKey =
  | 'contract_id'
  | 'contract_status'
  | 'contract_product'
  | 'contract_product_label'
  | 'insurer';

/** The contract-specific part of the detail page's rule context. */
export function buildContractDetailRuleContext(
  idNpol: string,
  detail: ContractDetail | null | undefined,
): Record<ContractDetailContextKey, string | undefined> {
  return {
    contract_id: idNpol === '' ? undefined : idNpol,
    contract_status: detail?.statut ?? undefined,
    contract_product: detail?.codeProduit ?? undefined,
    contract_product_label: detail?.produit ?? undefined,
    insurer: detail?.assureur ?? undefined,
  };
}
